export type NonEmptyList<T> = [T, ...T[]]

export type Prettify<T> = { [K in keyof T]: T[K] } & {}

/** copypasta from https://stackoverflow.com/a/57103940 */
export type DistributiveOmit<T, K extends keyof any> = T extends any
  ? Omit<T, K>
  : never

export function mapRecord
  <R extends {}, K extends keyof R, V extends R[K], MappedValue>
  (record: R, map: (key: K, value: V) => MappedValue)
{
  return (Object.entries(record) as [K, V][])
          .reduce((newRecord, [key, value]) => ({ ...newRecord, [key]: map(key, value) }),
                  {} as Record<K, MappedValue>)
}

export function mapRecordToList
  <R extends Record<any,any>, K extends keyof R, V extends R[K], MappedValue>
  (record: R, map: (key: K, value: V) => MappedValue)
{
  const list = []
  for (const key in record) {
    const value = record[key]
    const mappedValue = map(key as unknown as K, value)
    list.push(mappedValue)
  }
  return list
}

export function forEachEntry
  <R extends Record<any, any>, K extends keyof R, V extends R[K]>
  (record: R, fn: (key: K, value: V) => void)
{
  for (const key in record) {
    const value = record[key]
    fn(key as unknown as K, value)
  }
}

export function removeNullish<T>(values: T[]): NonNullable<T>[] {
  return values.filter((v): v is T => v != null) as NonNullable<T>[]
}

export function recordKeys<T extends Record<PropertyKey, unknown>>(obj: T): (keyof T)[] {
  return Object.keys(obj) as (keyof T)[]
}

export function recordValues<T extends Record<PropertyKey, unknown>>(obj: T): T[keyof T][] {
  return Object.values(obj) as T[keyof T][]
}

export function asComplete<T>(obj: Partial<T>): T {
  return obj as T
}

/**
 * This evaluates `X == Y`.
 * - see `_TestCases_Equals` for accounted test cases.
 * - [source of inspiration](https://github.com/microsoft/TypeScript/issues/37314#issuecomment-598459316)
 * - [commit that adds regression tests in TypeScript codebase](https://github.com/microsoft/TypeScript/pull/42525)
 * 
 * Regarding the implementation (please do correct this if wrong)
 * - It evaluates something like `(SubX ⊆ X) ∧ (SubY ⊆ Y) ∧ (( <SubX>() => SubX ) = ( <SubY>() => SubY ))`
 * - which is equal to `(SubX ⊆ X) ∧ (SubY ⊆ Y) ∧ ( SubX = SubY )`
 */
type Equals<X extends any, Y extends any> =
  (
    <SubX>() => (SubX extends X ? 1 : 0)
  ) extends (
    <SubY>() => (SubY extends Y ? 1 : 0)
  )
    ? true
    : false

/** TypeScript gives a type-error if these any of these test-cases fail. */
type _TestCases_Equals // eslint-disable-line @typescript-eslint/no-unused-vars
  = ExpectExtends<Equals<1337,420>, false>
  & ExpectExtends<Equals<1337,1337>, true>
  & ExpectExtends<Equals<{type: 'a'}, {type: 'b'}>, false>
  & ExpectExtends<Equals<{type: 'a'}, {type: 'a'}>, true>
  & ExpectExtends<Equals<'a', 'a' | 'b'>, false>
  & ExpectExtends<Equals<'a' | 'b', 'a'>, false>
  & ExpectExtends<Equals<'a' | 'b', 'a' | 'b'>, true>

/** TypeScript gives a type-error when `Given` does not extend `Expected`. */
export type ExpectExtends<Given extends Expected, Expected> = Given extends any ? 'yay' : 'nay'

export type Override<T, R> = Omit<T, keyof R> & R

type Primitive = string | number | boolean | bigint | symbol | null | undefined;

export type PartialDeep<T> =
  T extends Primitive
    ? T
    : T extends (...args: any[]) => any
    ? T
    : T extends ReadonlyArray<infer U>
    ? T extends (infer V)[]
      ? PartialDeep<V>[]
      : ReadonlyArray<PartialDeep<U>>
    : T extends Map<infer K, infer V>
    ? Map<PartialDeep<K>, PartialDeep<V>>
    : T extends Set<infer U>
    ? Set<PartialDeep<U>>
    : T extends object
    ? { [P in keyof T]?: PartialDeep<T[P]> }
    : T;